import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './ProjectAttachments.css';

const CHAT_API_BASE = '/api/chat';

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

const attIcon = (mime) => {
  if (!mime) return '📎';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('word')) return '📝';
  if (mime.includes('sheet') || mime.includes('excel')) return '📊';
  return '📎';
};

/**
 * Project-level file attachments. Uses the generic ledger-attachments.php
 * endpoint with parent_type='Projects'. Files attached to a Lead are copied
 * here automatically when the Lead is converted (see convert-lead.php).
 */
const ProjectAttachments = ({ projectId }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await authFetch(`${CHAT_API_BASE}/ledger-attachments.php?parent_type=Projects&parent_id=${encodeURIComponent(projectId)}`);
        const data = await res.json();
        if (!cancelled && data.success) setAttachments(data.data || []);
      } catch (e) {
        console.error('Failed to load attachments:', e);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const onChoose = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!file || !projectId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      fd.append('parent_type', 'Projects');
      fd.append('parent_id', projectId);
      const res = await authFetch(`${CHAT_API_BASE}/ledger-attachments.php`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success && data.data) {
        setAttachments(prev => [...prev, data.data]);
      } else {
        alert('Failed to attach file: ' + (data.message || 'unknown error'));
      }
    } catch (err) {
      console.error('Attachment upload failed:', err);
      alert('Failed to attach file.');
    }
    setUploading(false);
  };

  const remove = async (attId) => {
    setAttachments(prev => prev.filter(a => a.id !== attId));
    try {
      await authFetch(`${CHAT_API_BASE}/ledger-attachments.php?id=${encodeURIComponent(attId)}`, { method: 'DELETE' });
    } catch (e) { console.error('Failed to delete attachment:', e); }
  };

  return (
    <div className="project-attachments">
      <div className="project-attachments-head">
        <h3>Attachments</h3>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
          style={{ display: 'none' }}
          onChange={onChoose}
        />
        <button
          type="button"
          className="project-attach-btn"
          disabled={uploading}
          onClick={() => inputRef.current && inputRef.current.click()}
        >
          {uploading ? 'Uploading…' : '+ Attach file'}
        </button>
      </div>

      {loading ? (
        <p className="project-attachments-empty">Loading attachments…</p>
      ) : attachments.length === 0 ? (
        <p className="project-attachments-empty">No files attached to this project yet.</p>
      ) : (
        <ul className="project-attachments-list">
          {attachments.map(att => (
            <li key={att.id} className="project-attachment">
              <a href={att.file_url} target="_blank" rel="noopener noreferrer" title={att.name}>
                <span className="att-icon">{attIcon(att.mime)}</span>
                <span className="att-name">{att.name}</span>
              </a>
              <button type="button" className="att-del" onClick={() => remove(att.id)} title="Remove attachment">×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProjectAttachments;
